// AGC: Custom tooltip component for react-joyride (v6.9.0)
// AGC: Features: Origami mascot, progress indicator, animated hand pointer, prev/next buttons

import React, { useRef } from 'react'
import type { TooltipRenderProps } from 'react-joyride'
import { ChevronLeft, ChevronRight, X } from 'lucide-react'

// AGC: Click sound effect
const playClickSound = () => {
  try {
    const audio = new Audio('/sounds/click.mp3')
    audio.volume = 0.5 // AGC: Set volume to 50%
    audio.play().catch((error) => {
      // AGC: Silently fail if audio can't play (user interaction required, etc.)
      console.debug('AGC: Could not play click sound:', error)
    })
  } catch (error) {
    // AGC: Silently fail if audio creation fails
    console.debug('AGC: Could not create audio for click sound:', error)
  }
}

interface TourTooltipProps extends TooltipRenderProps {
  // AGC: Custom props
  totalSteps: number
  currentStep: number
  isAutoAdvancing: boolean
  autoAdvanceTimeLeft?: number
}

// AGC: Pointer Rock Mascot component
const OrigamiMascot: React.FC<{ size?: number }> = ({ size = 48 }) => (
  <div 
    className="relative flex-shrink-0"
    style={{ width: size, height: size }}
  >
    {/* AGC: Mascot container with glow effect */}
    <div className="absolute inset-0 bg-gradient-to-br from-cyan-400 to-purple-500 rounded-full opacity-30 blur-md animate-pulse" />
    <div className="relative w-full h-full rounded-full flex items-center justify-center shadow-lg border-2 border-white/30 overflow-hidden">
      {/* AGC: Pointer Rock image as mascot */}
      <img 
        src={`${import.meta.env.BASE_URL || '/'}pointerrock.png`}
        alt="Pointer Rock"
        className="w-full h-full object-contain"
        style={{ 
          imageRendering: 'crisp-edges',
          filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))'
        }}
      />
    </div>
    {/* AGC: Sparkle effects */}
    <div className="absolute -top-1 -right-1 w-3 h-3 bg-yellow-300 rounded-full animate-ping opacity-75" />
    <div className="absolute -bottom-1 -left-1 w-2 h-2 bg-cyan-300 rounded-full animate-ping opacity-75" style={{ animationDelay: '0.5s' }} />
  </div>
)

// AGC: Progress bar component
const ProgressBar: React.FC<{ current: number; total: number }> = ({ current, total }) => {
  const percent = Math.round(((current + 1) / total) * 100)
  
  return (
    <div className="w-full h-1.5 bg-slate-700 rounded-full overflow-hidden">
      <div 
        className="h-full bg-gradient-to-r from-cyan-400 to-purple-500 transition-all duration-300 ease-out"
        style={{ width: `${percent}%` }}
      />
    </div>
  )
}

// AGC: Main tooltip component
export const TourTooltip: React.FC<TourTooltipProps> = ({
  continuous,
  index,
  step,
  backProps,
  closeProps,
  primaryProps,
  skipProps,
  tooltipProps,
  totalSteps,
  currentStep,
  isAutoAdvancing,
}) => {
  const isFirstStep = index === 0
  const isLastStep = index === totalSteps - 1
  const showSkipButton = index >= 2 // AGC: Show skip after step 2
  
  return (
    <div
      {...tooltipProps}
      className="relative bg-slate-900/95 backdrop-blur-xl border border-cyan-500/30 rounded-2xl shadow-2xl shadow-cyan-500/20 p-0 max-w-sm w-full animate-in fade-in zoom-in duration-300"
      style={{ minWidth: '320px' }}
    >
      {/* AGC: Hand pointer image - positioned at spotlight (hidden if step.data.hideHand is true) */}
      {!(step as any)?.data?.hideHand && (
        <img 
          src={`${import.meta.env.BASE_URL || '/'}hand.png`}
          alt="pointer"
          className="absolute -bottom-16 left-1/2 transform -translate-x-1/2 w-12 h-12 animate-bounce pointer-events-none z-10"
          style={{ 
            filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.5))',
            animationDuration: '1s',
          }}
        />
      )}
      
      {/* AGC: Progress bar at top */}
      <div className="px-4 pt-4">
        <ProgressBar current={currentStep} total={totalSteps} />
      </div>
      
      {/* AGC: Header with mascot and title */}
      <div className="flex items-start gap-3 p-4 pb-2">
        <OrigamiMascot size={48} />
        <div className="flex-1 min-w-0">
          {step.title && (
            <h3 className="text-lg font-bold text-white mb-1 leading-tight">
              {step.title}
            </h3>
          )}
          <p className="text-xs text-cyan-300 font-medium">
            Step {currentStep + 1} of {totalSteps}
            {isAutoAdvancing && (
              <span className="ml-2 text-purple-300 animate-pulse">
                (auto-advancing...)
              </span>
            )}
          </p>
        </div>
        
        {/* AGC: Close button */}
        <button
          {...closeProps}
          onClick={(e) => {
            playClickSound()
            if (closeProps.onClick) closeProps.onClick(e)
          }}
          className="p-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-white transition-colors"
          aria-label="Close tour"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
      
      {/* AGC: Content */}
      <div className="px-4 pb-4">
        <div className="bg-slate-800/50 rounded-xl p-3 border border-slate-700/50">
          <p className="text-sm text-slate-200 leading-relaxed">
            {step.content}
          </p>
        </div>
        
        {/* AGC: Custom "Play Simulation" button for step 15 */}
        {(step as any)?.data?.showSimulationButton && (
          <div className="mt-3">
            <button
              onClick={() => {
                playClickSound()
                // AGC: Dispatch event to start simulation
                window.dispatchEvent(new CustomEvent('tour:select-simulation-mode'))
              }}
              className="w-full px-4 py-2.5 rounded-lg bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-400 hover:to-purple-500 text-white text-sm font-bold transition-all shadow-lg shadow-purple-500/30 hover:shadow-purple-500/50"
            >
              🎮 Play Simulation
            </button>
          </div>
        )}
      </div>
      
      {/* AGC: Navigation buttons */}
      <div className="flex items-center justify-between gap-2 px-4 pb-4 pt-2 border-t border-slate-800">
        {/* AGC: Left side - Skip button (after step 2) */}
        <div className="flex-1">
          {showSkipButton && (
            <button
              {...skipProps}
              onClick={(e) => {
                playClickSound()
                if (skipProps.onClick) skipProps.onClick(e)
              }}
              className="text-xs text-slate-500 hover:text-slate-300 transition-colors px-2 py-1"
            >
              Skip tour
            </button>
          )}
        </div>
        
        {/* AGC: Right side - Prev/Next buttons */}
        <div className="flex items-center gap-2">
          {!isFirstStep && (
            <button
              {...backProps}
              onClick={(e) => {
                playClickSound()
                if (backProps.onClick) backProps.onClick(e)
              }}
              className="flex items-center gap-1 px-3 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white text-sm font-medium transition-all"
            >
              <ChevronLeft className="w-4 h-4" />
              Back
            </button>
          )}
          
          {continuous && (
            <button
              {...primaryProps}
              onClick={(e) => {
                playClickSound()
                // AGC: Step 20 (index 19) - Next button should trigger start button click
                // AGC: This ensures the battle starts and tour waits for UI to load
                if (currentStep === 19) {
                  e.preventDefault()
                  e.stopPropagation()
                  console.log('AGC: Next clicked on step 20 - dispatching custom event')
                  window.dispatchEvent(new CustomEvent('tour:step20-next'))
                  return
                }
                // AGC: Step 26 (index 25) - Next button should trigger submit turn click
                if (currentStep === 25) {
                  e.preventDefault()
                  e.stopPropagation()
                  console.log('AGC: Next clicked on step 26 - dispatching custom event')
                  window.dispatchEvent(new CustomEvent('tour:step26-next'))
                  return
                }
                if (primaryProps.onClick) primaryProps.onClick(e)
              }}
              className="flex items-center gap-1 px-4 py-2 rounded-lg bg-gradient-to-r from-cyan-500 to-purple-600 hover:from-cyan-400 hover:to-purple-500 text-white text-sm font-bold transition-all shadow-lg shadow-purple-500/30 hover:shadow-purple-500/50"
            >
              {isLastStep ? 'Finish' : 'Next'}
              {!isLastStep && <ChevronRight className="w-4 h-4" />}
            </button>
          )}
        </div>
      </div>
      
      {/* AGC: Decorative corner accents */}
      <div className="absolute top-0 left-0 w-8 h-8 border-l-2 border-t-2 border-cyan-500/50 rounded-tl-2xl pointer-events-none" />
      <div className="absolute bottom-0 right-0 w-8 h-8 border-r-2 border-b-2 border-purple-500/50 rounded-br-2xl pointer-events-none" />
    </div>
  )
}

export default TourTooltip

